import React, { useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { assertPublicRecoveryJwk } from './payloadSafety.ts';
import { formatDateTime } from './recoveryUi.ts';
import type {
  GetMembershipCustodyPublicKey,
  RecoveryAssignment,
  RecoveryInvitation,
  RespondToMembershipInput,
} from './types.ts';

interface InvitationCardProps {
  invitation: RecoveryInvitation;
  getCustodyPublicKeyJwk?: GetMembershipCustodyPublicKey;
  onRespond: (
    invitation: RecoveryInvitation,
    input: RespondToMembershipInput
  ) => Promise<void>;
}

function InvitationCard({
  invitation,
  getCustodyPublicKeyJwk,
  onRespond,
}: InvitationCardProps) {
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const respond = async (decision: 'accept' | 'decline') => {
    setBusy(decision);
    setError(null);
    try {
      if (decision === 'accept') {
        if (!getCustodyPublicKeyJwk) {
          throw new Error(
            'Accepting is unavailable until device crypto can supply a custody public key.'
          );
        }
        const custodyPublicKeyJwk = await getCustodyPublicKeyJwk(invitation);
        if (!custodyPublicKeyJwk?.kty) {
          throw new Error(
            'This device did not provide a valid custody public key. The invitation was not accepted.'
          );
        }
        assertPublicRecoveryJwk(
          custodyPublicKeyJwk,
          'The membership custody key'
        );
        await onRespond(invitation, {
          decision,
          custodyPublicKeyJwk,
        });
      } else {
        await onRespond(invitation, { decision });
      }
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : `Could not ${decision} this invitation.`
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="h-100 recovery-action-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-2">
          <Card.Title as="h4" className="h5">
            {invitation.ownerLabel} named you as a keyholder
          </Card.Title>
          <Badge bg="warning" text="dark">Invitation</Badge>
        </div>
        <p>
          This circle has <strong>{invitation.memberCount} members</strong> and
          requires <strong>{invitation.threshold} approvals</strong>.
        </p>
        <p>
          Institution disclosure:{' '}
          <strong>
            {invitation.institutionPresent
              ? 'at least one institution is present'
              : 'no institution is present'}
          </strong>
          .
        </p>
        {invitation.revealMemberNames && invitation.memberNames?.length ? (
          <p>
            Named roster: {invitation.memberNames.join(', ')}
          </p>
        ) : (
          <p className="text-body-secondary">
            Member names are private. The stated size and threshold remain
            visible so no silent keyholder can be hidden.
          </p>
        )}
        <p className="small text-body-secondary">
          Invited {formatDateTime(invitation.invitedAt)}
        </p>
        <p className="small">
          Accepting lets this device hold encrypted custody and approve a named
          recovery request. It does not grant access to messages or authority
          over the owner.
        </p>

        {!getCustodyPublicKeyJwk && (
          <Alert variant="warning">
            Accept is disabled: this page has no device-crypto callback for a
            custody public key. Declining remains available.
          </Alert>
        )}
        <div className="d-flex flex-wrap gap-2">
          <Button
            onClick={() => respond('accept')}
            disabled={Boolean(busy) || !getCustodyPublicKeyJwk}
          >
            {busy === 'accept' ? 'Accepting…' : 'Accept role'}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => respond('decline')}
            disabled={Boolean(busy)}
          >
            {busy === 'decline' ? 'Declining…' : 'Decline'}
          </Button>
        </div>
        <div aria-live="polite" aria-atomic="true">
          {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
        </div>
      </Card.Body>
    </Card>
  );
}

function AssignmentCard({ assignment }: { assignment: RecoveryAssignment }) {
  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-2">
          <Card.Title as="h4" className="h5">
            Supporting {assignment.ownerLabel}
          </Card.Title>
          <Badge bg={assignment.status === 'accepted' ? 'success' : 'secondary'}>
            {assignment.status === 'accepted' ? 'Accepted' : assignment.status}
          </Badge>
        </div>
        <p>
          Any <strong>{assignment.threshold} of {assignment.memberCount}</strong>{' '}
          keyholders must approve.
        </p>
        <p>
          {assignment.institutionPresent
            ? 'At least one institution is disclosed in this circle.'
            : 'No institution is disclosed in this circle.'}
        </p>
        {assignment.revealMemberNames && assignment.memberNames?.length ? (
          <p>Named roster: {assignment.memberNames.join(', ')}</p>
        ) : (
          <p className="text-body-secondary">The owner keeps member names private.</p>
        )}
        {assignment.localKeyPresent === false && (
          <Alert variant="danger">
            This browser profile does not have the custody key for this
            assignment. Another profile will not have it, and it will not be
            regenerated silently.
          </Alert>
        )}
        {assignment.rotationRequired && (
          <Alert variant="warning" className="mb-0">
            The owner’s recovery key was consumed. After they rotate, this
            assignment keeps the same custody key. If this browser is missing
            that key, ask for a new invitation; it will not be regenerated
            silently.
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}

export interface KeyholderAssignmentsProps {
  invitations: RecoveryInvitation[];
  assignments: RecoveryAssignment[];
  getCustodyPublicKeyJwk?: GetMembershipCustodyPublicKey;
  onRespond: (
    invitation: RecoveryInvitation,
    input: RespondToMembershipInput
  ) => Promise<void>;
}

export default function KeyholderAssignments({
  invitations,
  assignments,
  getCustodyPublicKeyJwk,
  onRespond,
}: KeyholderAssignmentsProps) {
  return (
    <section aria-labelledby="keyholder-work-heading">
      <h2 id="keyholder-work-heading" className="h3">
        People I support
      </h2>
      <p>
        Keyholding is request-specific custody, not permission to read someone
        else’s conversations.
      </p>

      <h3 className="h5 mt-4">Invitations</h3>
      {invitations.length ? (
        <Row className="g-3">
          {invitations.map((invitation) => (
            <Col lg={6} key={invitation.id}>
              <InvitationCard
                invitation={invitation}
                getCustodyPublicKeyJwk={getCustodyPublicKeyJwk}
                onRespond={onRespond}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <p className="recovery-empty">No keyholder invitations are waiting.</p>
      )}

      <h3 className="h5 mt-5">Current assignments</h3>
      {assignments.length ? (
        <Row className="g-3">
          {assignments.map((assignment) => (
            <Col lg={6} key={assignment.id}>
              <AssignmentCard assignment={assignment} />
            </Col>
          ))}
        </Row>
      ) : (
        <p className="recovery-empty">You are not supporting a recovery circle.</p>
      )}
    </section>
  );
}
